import { useState, useEffect } from "react";
import { Row, Col, Badge, Card, AutoComplete, Input, message, Table, Tooltip, Button, Spin } from "antd";
import asyncFetch from "../../utils/asyncFetch.js";
import { asArray, asObject, asString, splitString } from "../../utils/dataGuards";
import GraphViewer from "../../components/GraphViewer";
import { defaultData } from "./graph.js";
import ThridText from '../../components/ThridText';
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { Link } from 'react-router-dom';

const { Search } = Input;

const isValidChrom = (val) => /^(?:[1-9]|1[0-9]|2[0-2]|X|Y)$/.test(val);
const isDigits = (val) => /^\d+$/.test(val);
const isString = (val) => /^[A-Za-z]+$/.test(val);
const isValidClass = (val) => /^(?:VUS|LP|P|B|LB)$/.test(val);

const validateInput = (value) => {
    const v = String(value || "").trim();
    if (!v) return { ok: false, msg: "Input is empty." };

    const parts = v.split('_');
    if (parts.length !== 5) {
        return { ok: false, msg: "Input should have 5 parts separated by '_'." };
    }
    const [chrom, pos, ref, alt, classification] = parts;
    if (!isValidChrom(chrom)) {
        return { ok: false, msg: "Invalid chromosome. Use 1-22, X, or Y." };
    }
    if (!isDigits(pos)) {
        return { ok: false, msg: "Position should be a number." };
    }
    if (!isString(ref) || !isString(alt)) {
        return { ok: false, msg: "Reference and alternate alleles should be alphabetic strings." };
    }
    if (!isValidClass(classification)) {
        return { ok: false, msg: "Classification should be one of VUS, LP, P, B, LB." };
    }
    return { ok: true, msg: "" };
}

const RGnetPage = () => {
    const [hintList, setHintList] = useState([]);
    const [data, setData] = useState({ nodes: [], links: [], categories: [] });
    const [highlightId, setHighlightId] = useState('');
    const [pm3, setPM3] = useState('NA');
    const [related, setRelated] = useState([]);
    const [visiable, setVisiable] = useState(true);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setData(defaultData);
    }, []);

    const queryHints = (value) => {
        asyncFetch('GET', '/hint/RGnet', { search_key: value, filter: true },
            (res) => {
                if (res.code === 200) {
                    setHintList(asArray(res.data).map(item => {
                        const value = asString(item);
                        return { value, label: value };
                    }).filter(item => item.value));
                } else {
                    message.error('Failed to fetch hints: ' + res.message);
                }
            },
            {}, 'cors', {}, false);
    };

    const queryGraph = (value) => {
        const check = validateInput(value);
        if (!check.ok) {
            message.error('Invalid input: ' + check.msg);
            return;
        };

        setLoading(true);
        asyncFetch('GET', '/rgnet', { Source: value },
            (res) => {
                if (res.code === 200) {
                    const graphData = asObject(res.data);
                    setData({
                        nodes: asArray(graphData.nodes),
                        links: asArray(graphData.links),
                        categories: asArray(graphData.categories)
                    });
                    setHighlightId(value);
                    setPM3(res.pm3 || 'NA');
                    setRelated(asArray(res.related));
                    setLoading(false);
                } else {
                    setLoading(false);
                    message.error({ content: 'Failed to load graph: ' + res.message });
                }
            },
            {}, 'cors', () => setLoading(false), false);
    };

    const handleNodeClick = (nodeName) => {
        const check = validateInput(nodeName);
        if (!check.ok) {
            message.error('Invalid input: ' + check.msg);
            return;
        };
        queryGraph(nodeName);
    };

    return (
        <Row justify="center" align="middle">
            <Col span={24} className="page-top-seize-seat" />
            <Spin spinning={loading} size="large" tip="Loading...">
                <div style={{ width: '100vw', height: 'calc(100vh - 134px)', position: 'relative' }}>
                    { data.categories.length > 0 && <GraphViewer highlightNodeId={highlightId} data={data} onNodeClick={handleNodeClick} /> }
                    <div
                        style={{
                            position: 'absolute',
                            top: 20,
                            right: 40,
                            zIndex: 1001
                        }}
                    >
                        {
                            visiable ? (
                                <Tooltip title="Hide">
                                    <Button
                                        type="primary"
                                        shape="circle"
                                        size="large"
                                        icon={<MinusOutlined />}
                                        onClick={() => setVisiable(false)}
                                    />
                                </Tooltip>
                            ) : (
                                <Tooltip title="Show">
                                    <Button
                                        type="primary"
                                        shape="circle"
                                        size="large"
                                        icon={<PlusOutlined />}
                                        onClick={() => setVisiable(true)}
                                    />
                                </Tooltip>
                            )
                        }
                    </div>
                    {
                        visiable && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 70,
                                    right: 40,
                                    width: '25vw',
                                    minWidth: '240px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                    borderRadius: '8px',
                                }}
                            >
                                <Badge.Ribbon text="GRCh37" color="red">
                                    <Card>
                                        <AutoComplete
                                            options={hintList}
                                            onSelect={queryGraph}
                                            onChange={queryHints}
                                            style={{ width: '100%', marginTop: '14px' }}
                                        >
                                            <Search
                                                placeholder="e.g. 13_20763095_T_G_VUS"
                                                size="large"
                                                onSearch={queryGraph}
                                            />
                                        </AutoComplete>
                                        <Row style={{ margin: '20px 0' }}>
                                            <Col span={12}>
                                                <span className='variant-thrid-title'>
                                                    RGnet Count:
                                                </span>
                                                <ThridText contact={related.length} color="#02709b" background="#fff" />
                                            </Col>
                                            <Col span={12}>
                                                <span className='variant-thrid-title'>
                                                    PM3 evdience:
                                                </span>
                                                <ThridText contact={pm3} color="#02709b" background="#fff" />
                                            </Col>
                                        </Row>
                                        <Table
                                            columns={[
                                                { title: 'Gene', dataIndex: 'Gene', key: 'gene', width: 100 },
                                                { title: 'source', dataIndex: 'Source', key: 'source', render: text => {
                                                    const parts = splitString(text, '_');
                                                    if (parts.length < 4) return asString(text, '-');
                                                    const site = parts.slice(0, 4).join('-');
                                                    return  (
                                                        <i>
                                                            <Link style={{color: '#02709b'}} to={"/variant/GRCh37/" + site}>{ text }</Link>
                                                        </i>
                                                    );
                                                }},
                                                { title: 'target', dataIndex: 'Target', key: 'target', render: text => {
                                                    const parts = splitString(text, '_');
                                                    if (parts.length < 4) return asString(text, '-');
                                                    const site = parts.slice(0, 4).join('-');
                                                    return  (
                                                        <i>
                                                            <Link style={{color: '#02709b'}} to={"/variant/GRCh37/" + site}>{ text }</Link>
                                                        </i>
                                                    );
                                                }},
                                                { title: 'Weight', dataIndex: 'Weight', key: 'weight', width: 80 },
                                            ]}
                                            dataSource={related}
                                            pagination={false}
                                            size="small"
                                            scroll={{x: 1200, y: 250}}
                                        />
                                    </Card>
                                </Badge.Ribbon>
                            </div>
                        )
                    }
                </div>
            </Spin>
        </Row>
    );
}

export default RGnetPage;
